import Backbone from 'backbone';
import { View } from 'backbone.marionette';
import _ from 'lodash';

import showErrorMessage from '@/util/showErrorMessage';
import showSuccessMessage from '@/util/ShowSuccessMessage';

import ReloadViewModule from '../../../services/ReloadViewModule';
import SaveCustomerOrderDevices from '../../../services/SaveCustomerOrderDevices';
import UpdateOrderLineItems from '../../../services/UpdateOrderLineItems';
import PRTNRDeviceSectionView from '../../../views/PRTNRDeviceSectionView';

import tmplPRTNRFormView from './PRTNRProdFormView.hbs';

const PRTNRProdFormView = View.extend({
  template: tmplPRTNRFormView,

  ui: {
    prodDescriptionSection: '.prod-description-section',
    prodNIPRSection: '.prod-nipr-section',
    prodSIPRSection: '.prod-sipr-section',
    prodServicesSection: '.prod-services-section',
    devicesAlert: '.no-devices-error'
  },

  regions: {
    prodDescriptionSection: '@ui.prodDescriptionSection',
    prodNIPRSection: '@ui.prodNIPRSection',
    prodSIPRSection: '@ui.prodSIPRSection',
    prodServicesSection: '@ui.prodServicesSection'
  },

  className: 'prtnr-form-view',

  onRender() {
    const orderType = this.model.get('orderDetails').orderTypeId;
    const isAdminEdit = this.model.get('orderDetails').isAdminEdit;

    const { ProdNIPR, ProdSIPR, ProdServices } = _.groupBy(this.model.get('lineItems'), 'panel');

    this.showChildView(
      'prodDescriptionSection',
      new PRTNRDeviceSectionView({
        model: new Backbone.Model({
          title: 'Device Information',
          type: 'description',
          contract: 'Prod',
          id: 'Prod-Description',
          isAdminEdit
        })
      })
    );

    this.showChildView(
      'prodNIPRSection',
      new PRTNRDeviceSectionView({
        model: new Backbone.Model({
          lineItems: ProdNIPR,
          title: 'NIPR Devices',
          orderType,
          type: 'devices',
          contract: 'Prod',
          id: 'Prod-NIPR',
          isAdminEdit
        })
      })
    );

    this.showChildView(
      'prodSIPRSection',
      new PRTNRDeviceSectionView({
        model: new Backbone.Model({
          lineItems: ProdSIPR,
          title: 'SIPR Devices',
          orderType,
          type: 'devices',
          contract: 'Prod',
          id: 'Prod-SIPR',
          isAdminEdit
        })
      })
    );

    this.showChildView(
      'prodServicesSection',
      new PRTNRDeviceSectionView({
        model: new Backbone.Model({
          lineItems: ProdServices,
          title: 'Additional Services',
          orderType,
          type: 'services',
          contract: 'Prod',
          id: 'Prod-Services',
          isAdminEdit
        })
      })
    );
  },

  showSaveError() {
    showErrorMessage({
      errorCode: 'saveError',
      errorTitle: 'Save Error',
      errorMessage:
        'An error occurred while saving devices for this order. Try again or contact an administrator if your issue persists.'
    });
  },

  showSaveSuccess() {
    showSuccessMessage({
      code: 'saveSuccess',
      title: 'Save Success',
      message: 'This order has been saved successfully.',
      hideAfter: 5
    });
  },
  
  showNoChangedDevicesError() {
    showErrorMessage({
      errorCode: 'noChangedDevicesError',
      errorTitle: 'No Devices Updated Error',
      errorMessage:
        'No devices were updated. Please update devices in order to save changes.'
    });
  },

  async validate() {
    const [niprColl, siprColl, servicesColl] = await this.getDeviceData();

    const totalMonoQuantity = niprColl.totalMonoQuantity + siprColl.totalMonoQuantity;
    const totalColorQuantity = niprColl.totalColorQuantity + siprColl.totalColorQuantity;

    let isValid = true;
    if (
      !totalMonoQuantity &&
      !totalColorQuantity &&
      !servicesColl.totalMonthlyAdditionalServicesQuantity &&
      !servicesColl.totalYearlyOneTimeServicesQuantity
    ) {
      isValid = false;
      this.ui.devicesAlert.show();
    }
    isValid ? this.trigger('no:errors') : this.trigger('has:errors');
    return isValid;
  },

  async getDeviceData() {
    return await Promise.all([
      this.getChildView('prodNIPRSection').getDeviceData(),
      this.getChildView('prodSIPRSection').getDeviceData(),
      this.getChildView('prodServicesSection').getDeviceData()
    ]);
  },

  getLineItemData(lineItemData) {
    const lineItems = this.model.get('lineItems');
    const changedItems = [];

    _.each(lineItems, (item) => {
      const changedPrice = lineItemData[`${item.id}-price`];
      const changedConfig = lineItemData[`${item.id}-configurationAndPricing`];
      const changedDesc = lineItemData[`${item.id}-description`];
      const changedDlaCost = lineItemData[`${item.id}-dlaCost`];

      const isPriceChanged = (item.price !== changedPrice) && item.price;
      const isConfigChanged = (item.configurationAndPricing !== changedConfig) && item.configurationAndPricing;
      const isDescChanged = (item.description !== changedDesc) && !!item.description
      const isDlaCostChanged = (item.dlaCost !== changedDlaCost) && !!item.dlaCost;

      const changedItem = {};
      if (isPriceChanged) changedItem.price = (parseFloat(changedPrice)).toFixed(2);
      if (isConfigChanged) changedItem.configurationAndPricing = changedConfig;
      if (isDescChanged) changedItem.description = changedDesc;
      if (isDlaCostChanged) changedItem.dlaCost = changedDlaCost;
      if (!_.isEmpty(changedItem)) {
        changedItem.id = item.id;
        changedItems.push(changedItem);
      }
    })

    return changedItems;
  },

  async onSave(lineItemData) {
    this.ui.devicesAlert.hide();
    this.trigger('no:errors');
    let formData;
    const isAdminEdit = this.model.get('orderDetails').isAdminEdit;

    try {
      if (isAdminEdit && lineItemData) {
        formData = await this.saveAdminEdit(lineItemData);
        if (formData.changedItems.length > 0) {
          await UpdateOrderLineItems(formData);
          this.showSaveSuccess();
        } else {
          this.showNoChangedDevicesError();
        }
      } else {
        formData = await this.saveOrder();
        await Promise.all([SaveCustomerOrderDevices(formData), ReloadViewModule.reload()]);
        this.showSaveSuccess();
      }
    } catch (err) {
      this.showSaveError();
      throw new Error(err);
    }
  },

  async saveOrder() {
    const [niprColl, siprColl, servicesColl] = await this.getDeviceData();

    const devicesFormData = niprColl.changedItems.concat(siprColl.changedItems, servicesColl.changedItems);

    const formData = {
      devicesFormData,
      orderId: this.model.get('orderDetails').orderId,
      orderTypeId: this.model.get('orderDetails').orderTypeId,
      itPocExists: true
    };

    return formData;
  },

  async saveAdminEdit(lineItemData) {
    const changedItems = this.getLineItemData(lineItemData);
    const formData = {
      orderFormTypeID: this.model.get('orderDetails').orderTypeId,
      changedItems
    };

    return formData;
  }
});

export default PRTNRProdFormView;
